import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import { SUBURBS, SERVICES, BRAND } from "@/lib/site";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import QuoteForm from "@/components/QuoteForm";
import MapEmbed from "@/components/MapEmbed";
import CallStrip from "@/components/CallStrip";

const SITE_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  "https://flowgridplumbing.com.au";

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = SUBURBS.find((s) => s.slug === slug);
  if (!suburb) return <Navigate to="/service-areas" replace />;

  return (
    <>
      <SEO
        title={`Plumber ${suburb.name} — 24/7 Local Plumbing in ${suburb.name}, NSW`}
        description={`Local plumber in ${suburb.name}, NSW. ${suburb.blurb} Licensed NSW plumbers & gas fitters. 24/7 emergency response. Call ${BRAND.phone}.`}
        canonicalPath={`/service-areas/${suburb.slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Place",
            name: `${suburb.name}, NSW`,
            geo: {
              "@type": "GeoCoordinates",
              latitude: suburb.coords[0],
              longitude: suburb.coords[1],
            },
            containedInPlace: {
              "@type": "AdministrativeArea",
              name: "Sydney, NSW, Australia",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: `Plumber ${suburb.name}`,
            provider: {
              "@type": "Plumber",
              name: BRAND.name,
              telephone: BRAND.phone,
              email: BRAND.email,
            },
            areaServed: {
              "@type": "Place",
              name: `${suburb.name}, NSW`,
            },
            url: `${SITE_URL}/service-areas/${suburb.slug}`,
            description: suburb.blurb,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: SITE_URL,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Service Areas",
                item: `${SITE_URL}/service-areas`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: suburb.name,
                item: `${SITE_URL}/service-areas/${suburb.slug}`,
              },
            ],
          },
        ]}
      />
      <PageHeader
        eyebrow={`Plumber ${suburb.name}`}
        title={`Local plumber in ${suburb.name}, NSW`}
        subtitle={suburb.blurb}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={BRAND.phoneTel}
            data-testid="suburb-call-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-bold text-white hover:bg-blue-700"
          >
            <Phone className="h-5 w-5" /> Call {BRAND.phone}
          </a>
          <a
            href="#quote"
            data-testid="suburb-quote-btn"
            className="inline-flex items-center gap-2 rounded-lg border-2 border-white/80 px-6 py-3 font-bold text-white hover:bg-white hover:text-slate-900"
          >
            Get Free Quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </PageHeader>

      <section className="bg-white py-20 md:py-24">
        <div className="fg-container grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
              All services in {suburb.name}
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900">
              One local crew. Every plumbing job.
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`suburb-service-${s.slug}`}
                    className="group fg-card-hover flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 group-hover:bg-blue-600 group-hover:text-white">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900">{s.title}</div>
                      <div className="text-xs text-slate-500">{s.short}</div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-12">
              <MapEmbed height={360} />
            </div>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                Why {suburb.name} chooses Flowgrid
              </div>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  `Rapid response across ${suburb.name}`,
                  "Upfront fixed pricing",
                  "Licensed NSW plumbers & gas fitters",
                  "Workmanship guarantee in writing",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-md">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                <MapPin className="h-3.5 w-3.5" /> {suburb.name}, NSW
              </div>
              <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">
                Free quote in {suburb.name}
              </h3>
              <div className="mt-5">
                <QuoteForm compact idPrefix={`sub-${slug}`} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CallStrip />
    </>
  );
}
